import React from 'react';
import { redirect } from '../../../helper/redirectHelper';
import { Container, Entry, SubTitle, Title } from './APIReferencePage.styled';
import Terms from '../../components/common/Terms';

interface IEndpoint {
    heading: string;
    viewers?: string;
    comment: string;
    pathUrl: string;
    pathLabel: string;
}

const endpoints: IEndpoint[] = [
    {
        heading: '/data/address',
        viewers: '',
        comment: 'Returns a list of addresses. For privacy reasons the full address data is not provided.',
        pathUrl: 'data/address',
        pathLabel: '/data/address',
    },
    {
        heading: '/data/address/{signature}',
        viewers: '',
        comment:
            'Returns an address identified by a hash of the data from the address. For privacy reasons the full address is not provided.',
        pathUrl: 'data/address/D7F011312F277A70579BF34047F69093',
        pathLabel: '/data/address/D7F011312F277A70579BF34047F69093',
    },
    {
        heading: '/data/publication',
        viewers: 'short normal',
        comment: 'Returns bibliographic data from patent publications.',
        pathUrl: 'data/publication',
        pathLabel: '/data/publication',
    },
    {
        heading: '/data/publication/{st3Code}',
        viewers: 'short normal',
        comment:
            'Returns bibliographic data from publications published by a publication authority identified by its WIPO ST.3 code.',
        pathUrl: 'data/publication/EP',
        pathLabel: '/data/publication/EP',
    },
    {
        heading: '/data/publication/{st3Code}/{publicationNumber}',
        viewers: 'short normal',
        comment:
            'Returns data for all publications for a patent identified by the WIPO ST.3 code of the publication authority and its publication number',
        pathUrl: 'data/publication/EP/1048543',
        pathLabel: '/data/publication/EP/1048543',
    },
    {
        heading: '/data/publication/{st3Code}/{publicationNumber}/{pubKind}',
        viewers: 'short normal',
        comment:
            'Returns bibliographic data from a specific kind of publication for a specific application identified by its publication number. There may be more than one publication.',
        pathUrl: 'data/publication/EP/1010425/A9',
        pathLabel: '/data/publication/EP/1010425/A9',
    },
    {
        heading: '/data/publication/{st3Code}/{publicationNumber}/{pubKind}/{pubDate}',
        viewers: 'normal',
        comment:
            "Returns bibliographic data from a patent publication identified by its application authority {st3}, publication number, publication kind (A1, B2 etc) and publication date. The date component should be '-' in all cases except EPO A8, A9, B3, B8 and B9 publications, WO A3 publications, DE T3 publications and EA B2 publications.",
        pathUrl: 'data/publication/EP/1010425/A9/2003-12-17',
        pathLabel: '/data/publication/EP/1010425/A9/2003-12-17',
    },
    {
        heading: '/data/publication/{st3Code}/{publicationNumber}/{pubKind}/{pubDate}/citation',
        viewers: 'normal',
        comment:
            "Returns data for the citations for a publication. The publication is identified by the WIPO ST.3 code of the publication's publication authority, its publication number, publication kind and publication date as described the publication endpoint.",
        pathUrl: 'data/publication/EP/1100000/A3/-/citation',
        pathLabel: '/data/publication/EP/1100000/A3/-/citation',
    },
    {
        heading: '/data/publication/{st3Code}/{publicationNumber}/{pubKind}/{pubDate}/citation/{hash}',
        viewers: '',
        comment:
            'Returns data for a particular citation. The publication with the citation is identified {st3Code}, {publicationNumber}, {pubKind} and {pubDate} as for the publication resource endpoint. The particular citation is identified by its hash value.',
        pathUrl: 'data/publication/EP/1100000/A3/-/citation/97322ADB283F4EAC84A445EAAD81F1A0',
        pathLabel: '/data/publication/EP/1100000/A3/-/citation/97322ADB283F4EAC84A445EAAD81F1A0',
    },
    {
        heading: '/data/simple-family',
        viewers: 'normal',
        comment: 'Returns a list of simple families',
        pathUrl: 'data/simple-family',
        pathLabel: '/data/simple-family',
    },
    {
        heading: '/data/simple-family/{familyId}',
        viewers: 'normal',
        comment: 'Returns data about a simple simple family.',
        pathUrl: 'data/simple-family/23355309',
        pathLabel: '/data/simple-family/23355309',
    },
    {
        heading: '/data/vc',
        viewers: 'normal',
        comment: 'Returns a list of vcards.',
        pathUrl: 'data/vc',
        pathLabel: '/data/vc',
    },
    {
        heading: '/data/vc/{signature}',
        viewers: 'normal',
        comment:
            'Returns name and address information from a VCard identified by a hash of the vcard data. For privacy reasons a full address is not given.',
        pathUrl: 'data/vc/E698F35344F07B81434CD5A534168B8E',
        pathLabel: '/data/vc/E698F35344F07B81434CD5A534168B8E',
    },
    {
        heading: '/def/',
        viewers: 'none scheme',
        comment: 'Returns a list of vocabularies organised under https://data.epo.org/def/',
        pathUrl: 'def',
        pathLabel: '/def/',
    },
    {
        heading: '/def/cpc/scheme',
        viewers: 'normal',
        comment: 'Returns data about the CPC scheme',
        pathUrl: 'def/cpc/scheme',
        pathLabel: '/def/cpc/scheme',
    },
    {
        heading: '/def/cpc/{symbol}',
        viewers: 'normal',
        comment: 'Returns data about a CPC classification or other node.',
        pathUrl: 'def/cpc/A',
        pathLabel: '/def/cpc/A',
    },
    {
        heading: '/def/ipc/{symbol}',
        viewers: 'normal',
        comment: 'Returns data about an IPC classification',
        pathUrl: 'def/ipc/A',
        pathLabel: '/def/ipc/A',
    },
    {
        heading: '/def/st3/conceptScheme',
        viewers: 'normal',
        comment: 'returns data about the WIPO ST3 controlled vocabulary of countries and organizations.',
        pathUrl: 'def/st3/conceptScheme',
        pathLabel: '/def/st3/conceptScheme',
    },
    {
        heading: '/def/{scheme}',
        viewers: 'definition',
        comment: 'Returns a paged list of vocabulary terms from a given vocabulary.',
        pathUrl: 'def/patent',
        pathLabel: '/def/patent',
    },
    {
        heading: '/def/{scheme}/',
        viewers: 'scheme',
        comment: 'Returns information about a particular vocabulary.',
        pathUrl: 'def/patent/',
        pathLabel: '/def/patent/',
    },
    {
        heading: '/def/{scheme}/{term}',
        viewers: 'definition property class skos',
        comment: 'Returns individual term definitions from a vocabulary.',
        pathUrl: 'def/patent/Application',
        pathLabel: '/def/patent/Application',
    },
    {
        heading: '/doc/application',
        viewers: 'full short',
        comment:
            'Returns a list of applications. This endpoint will normally be used with filters to select the applications to return.',
        pathUrl: 'doc/application/applicationAuthority=EP',
        pathLabel: '/doc/application/applicationAuthority=EP',
    },
    {
        heading: '/doc/application/{st3Code}',
        viewers: 'full short',
        comment: 'Returns a list of applications identified by the WIPO ST.3 code of their application authority.',
        pathUrl: 'doc/application/EP',
        pathLabel: '/doc/application/EP',
    },
    {
        heading: '/doc/application/{st3Code}/{applicationNumber}',
        viewers: 'full short',
        comment:
            'Returns data about a patent application identified by the application authority (st3Code} and application number.',
        pathUrl: 'doc/application/EP/99203729',
        pathLabel: '/doc/application/EP/99203729',
    },
    {
        heading: '/doc/st3',
        viewers: '',
        comment: 'Returns a list of counties and organizations in the WIPO ST.3 controlled vocabulary.',
        pathUrl: 'doc/st3',
        pathLabel: '/doc/st3',
    },
    {
        heading: '/doc/st3/{st3Code}',
        viewers: '',
        comment: 'Returns information about a country or organization identified by its WIPO ST.3 code.',
        pathUrl: 'doc/st3/EP',
        pathLabel: '/doc/st3/EP',
    },
];

const APIReferencePage = () => {
    return (
        <Container className="container">
            <Title data-testid="title">API reference</Title>
            <p style={{ marginBottom: '1.5em' }}>
                This is the reference documentation for EPO linked data API. It lists the available endpoints, provides
                a brief description of the endpoint and a list of any specific viewers defined for the endpoint. The
                standard viewers, basic, describe, labelled describe and none (see the{' '}
                <a href="/linked-data/documentation/api-overview">api overview document</a> for a list of these) are available on
                all endpoints.
            </p>
            <div id="subsection">
                <SubTitle data-testid="endpoints">Endpoints</SubTitle>
                {endpoints.map((endpoint) => (
                    <Entry key={endpoint.pathLabel}>
                        <div className="heading">{endpoint.heading}</div>
                        <div>{endpoint.comment}</div>
                        {endpoint.viewers && <div>Viewers: {endpoint.viewers}</div>}
                        <div>
                            Example Request Path: <a href={redirect(endpoint.pathUrl)}>{endpoint.pathLabel}</a>
                        </div>
                    </Entry>
                ))}
            </div>
            <div id="shortnames">
                <SubTitle data-testid="shortNames">Short Names</SubTitle>
                <p>
                    The API is configured with a set of short names that identify classes and properties that it is
                    convenient to use in place of the longer and more verbose URLs that identify those classes and
                    properties.
                </p>
                <p>The following table lists those short names alongside the URLs they may be used to replace</p>
                <table data-testid="shortNamesTable">
                    <tbody>
                        <tr>
                            <th>Short Name</th>
                            <th>URL</th>
                        </tr>
                        <tr>
                            <td>Class</td>
                            <td>https://www.w3.org/2002/07/owl#Class</td>
                        </tr>
                        <tr>
                            <td>ConceptScheme</td>
                            <td>https://www.w3.org/2004/02/skos/core#ConceptScheme</td>
                        </tr>
                        <tr>
                            <td>Datatype</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#Datatype</td>
                        </tr>
                        <tr>
                            <td>abstract</td>
                            <td>https://purl.org/dc/terms/abstract</td>
                        </tr>
                        <tr>
                            <td>additionOf</td>
                            <td>https://data.epo.org/linked-data/def/patent/additionOf</td>
                        </tr>
                        <tr>
                            <td>agentVC</td>
                            <td>https://data.epo.org/linked-data/def/patent/agentVC</td>
                        </tr>
                        <tr>
                            <td>applicantVC</td>
                            <td>https://data.epo.org/linked-data/def/patent/applicantVC</td>
                        </tr>
                        <tr>
                            <td>application</td>
                            <td>https://data.epo.org/linked-data/def/patent/application</td>
                        </tr>
                        <tr>
                            <td>applicationAuthority</td>
                            <td>https://data.epo.org/def/patent/applicationAuthority</td>
                        </tr>
                        <tr>
                            <td>applicationKind</td>
                            <td>https://data.epo.org/def/patent/applicationKind</td>
                        </tr>
                        <tr>
                            <td>applicationNumber</td>
                            <td>https://data.epo.org/linked-data/def/patent/applicationNumber</td>
                        </tr>
                        <tr>
                            <td>applicationNumberEpodoc</td>
                            <td>https://data.epo.org/linked-data/def/patent/applicationNumberEpodoc</td>
                        </tr>
                        <tr>
                            <td>applicationNumberOriginal</td>
                            <td>https://data.epo.org/linked-data/def/patent/applicationNumberOriginal</td>
                        </tr>
                        <tr>
                            <td>broader</td>
                            <td>https://www.w3.org/2004/02/skos/core#broader</td>
                        </tr>
                        <tr>
                            <td>citation</td>
                            <td>https://data.epo.org/linked-data/def/patent/citation</td>
                        </tr>
                        <tr>
                            <td>citationNPL</td>
                            <td>https://data.epo.org/linked-data/def/patent/citationNPL</td>
                        </tr>
                        <tr>
                            <td>citationText</td>
                            <td>https://data.epo.org/linked-data/def/patent/citationText</td>
                        </tr>
                        <tr>
                            <td>citesPatentPublication</td>
                            <td>https://data.epo.org/linked-data/def/patent/citesPatentPublication</td>
                        </tr>
                        <tr>
                            <td>classificationCPCAdditional</td>
                            <td>https://data.epo.org/linked-data/def/patent/classificationCPCAdditional</td>
                        </tr>
                        <tr>
                            <td>classificationCPCInventive</td>
                            <td>https://data.epo.org/linked-data/def/patent/classificationCPCInventive</td>
                        </tr>
                        <tr>
                            <td>classificationIPCAdditional</td>
                            <td>https://data.epo.org/linked-data/def/patent/classificationIPCAdditional</td>
                        </tr>
                        <tr>
                            <td>classificationIPCInventive</td>
                            <td>https://data.epo.org/linked-data/def/patent/classificationIPCInventive</td>
                        </tr>
                        <tr>
                            <td>comment</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#comment</td>
                        </tr>
                        <tr>
                            <td>concordantCPC</td>
                            <td>https://data.epo.org/def/cpc/concordantCPC</td>
                        </tr>
                        <tr>
                            <td>concordantIPC</td>
                            <td>https://data.epo.org/linked-data/def/cpc/concordantIPC</td>
                        </tr>
                        <tr>
                            <td>corrects</td>
                            <td>https://data.epo.org/linked-data/def/patent/corrects</td>
                        </tr>
                        <tr>
                            <td>countryCode</td>
                            <td>https://data.epo.org/def/patent/countryCode</td>
                        </tr>
                        <tr>
                            <td>countryName</td>
                            <td>https://www.w3.org/2006/vcard/ns#country-name</td>
                        </tr>
                        <tr>
                            <td>cpc-scheme</td>
                            <td>https://data.epo.org/linked-data/def/cpc/scheme</td>
                        </tr>
                        <tr>
                            <td>cpcClassification</td>
                            <td>https://data.epo.org/linked-data/def/cpc/classification</td>
                        </tr>
                        <tr>
                            <td>cpcSymbol</td>
                            <td>https://data.epo.org/linked-data/def/cpc/symbol</td>
                        </tr>
                        <tr>
                            <td>date</td>
                            <td>https://purl.org/dc/terms/date</td>
                        </tr>
                        <tr>
                            <td>divisionalOf</td>
                            <td>https://data.epo.org/linked-data/def/patent/divisionalOf</td>
                        </tr>
                        <tr>
                            <td>domain</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#domain</td>
                        </tr>
                        <tr>
                            <td>family</td>
                            <td>https://data.epo.org/def/patent/familyMemberOf</td>
                        </tr>
                        <tr>
                            <td>familyMember</td>
                            <td>https://data.epo.org/linked-data/def/patent/familyMember</td>
                        </tr>
                        <tr>
                            <td>filingDate</td>
                            <td>https://data.epo.org/linked-data/def/patent/filingDate</td>
                        </tr>
                        <tr>
                            <td>filingLanguage</td>
                            <td>https://data.epo.org/def/patent/filingLanguage</td>
                        </tr>
                        <tr>
                            <td>fn</td>
                            <td>https://www.w3.org/2006/vcard/ns#fn</td>
                        </tr>
                        <tr>
                            <td>format</td>
                            <td>https://purl.org/dc/terms/format</td>
                        </tr>
                        <tr>
                            <td>fullTitle</td>
                            <td>https://data.epo.org/linked-data/def/cpc/fullTitle</td>
                        </tr>
                        <tr>
                            <td>grantDate</td>
                            <td>https://data.epo.org/linked-data/def/patent/grantDate</td>
                        </tr>
                        <tr>
                            <td>guidanceHeading</td>
                            <td>https://data.epo.org/def/cpc/guidanceHeading</td>
                        </tr>
                        <tr>
                            <td>hasAddress</td>
                            <td>https://www.w3.org/2006/vcard/ns#hasAddress</td>
                        </tr>
                        <tr>
                            <td>hasCorrection</td>
                            <td>https://data.epo.org/linked-data/def/patent/hasCorrection</td>
                        </tr>
                        <tr>
                            <td>hasTopConcept</td>
                            <td>https://www.w3.org/2004/02/skos/core#hasTopConcept</td>
                        </tr>
                        <tr>
                            <td>hasVersion</td>
                            <td>https://purl.org/dc/terms/hasVersion</td>
                        </tr>
                        <tr>
                            <td>internationalApplication</td>
                            <td>https://data.epo.org/linked-data/def/patent/internationalApplication</td>
                        </tr>
                        <tr>
                            <td>inventorVC</td>
                            <td>https://data.epo.org/linked-data/def/patent/inventorVC</td>
                        </tr>
                        <tr>
                            <td>ipcClassification</td>
                            <td>https://data.epo.org/linked-data/def/ipc/classification</td>
                        </tr>
                        <tr>
                            <td>ipcSymbol</td>
                            <td>https://data.epo.org/linked-data/def/ipc/symbol</td>
                        </tr>
                        <tr>
                            <td>isDefinedBy</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#isDefinedBy</td>
                        </tr>
                        <tr>
                            <td>isReplacedBy</td>
                            <td>https://purl.org/dc/terms/isReplacedBy</td>
                        </tr>
                        <tr>
                            <td>isVersionOf</td>
                            <td>https://purl.org/dc/terms/isVersionOf</td>
                        </tr>
                        <tr>
                            <td>label</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#label</td>
                        </tr>
                        <tr>
                            <td>language</td>
                            <td>https://purl.org/dc/terms/language</td>
                        </tr>
                        <tr>
                            <td>level</td>
                            <td>https://data.epo.org/linked-data/def/cpc/level</td>
                        </tr>
                        <tr>
                            <td>locality</td>
                            <td>https://www.w3.org/2006/vcard/ns#locality</td>
                        </tr>
                        <tr>
                            <td>member</td>
                            <td>https://www.w3.org/2004/02/skos/core#member</td>
                        </tr>
                        <tr>
                            <td>modified</td>
                            <td>https://purl.org/dc/terms/modified</td>
                        </tr>
                        <tr>
                            <td>narrower</td>
                            <td>https://www.w3.org/2004/02/skos/core#narrower</td>
                        </tr>
                        <tr>
                            <td>notation</td>
                            <td>https://www.w3.org/2004/02/skos/core#notation</td>
                        </tr>
                        <tr>
                            <td>priority</td>
                            <td>https://data.epo.org/def/patent/priority</td>
                        </tr>
                        <tr>
                            <td>proceduralLanguage</td>
                            <td>https://data.epo.org/linked-data/def/patent/proceduralLanguage</td>
                        </tr>
                        <tr>
                            <td>publication</td>
                            <td>https://data.epo.org/linked-data/def/patent/publication</td>
                        </tr>
                        <tr>
                            <td>publicationAuthority</td>
                            <td>https://data.epo.org/linked-data/def/patent/publicationAuthority</td>
                        </tr>
                        <tr>
                            <td>publicationDate</td>
                            <td>https://data.epo.org/linked-data/def/patent/publicationDate</td>
                        </tr>
                        <tr>
                            <td>publicationKind</td>
                            <td>https://data.epo.org/def/patent/publicationKind</td>
                        </tr>
                        <tr>
                            <td>publicationKindLabel</td>
                            <td>https://data.epo.org/def/patent/publicationKindLabel</td>
                        </tr>
                        <tr>
                            <td>publicationNumber</td>
                            <td>https://data.epo.org/linked-data/def/patent/publicationNumber</td>
                        </tr>
                        <tr>
                            <td>range</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#range</td>
                        </tr>
                        <tr>
                            <td>replaces</td>
                            <td>https://purl.org/dc/terms/replaces</td>
                        </tr>
                        <tr>
                            <td>representation</td>
                            <td>https://data.epo.org/linked-data/def/patent/representation</td>
                        </tr>
                        <tr>
                            <td>representativeFamilyMember</td>
                            <td>https://data.epo.org/linked-data/def/patent/representativeFamilyMember</td>
                        </tr>
                        <tr>
                            <td>seeAlso</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#seeAlso</td>
                        </tr>
                        <tr>
                            <td>sortKey</td>
                            <td>https://data.epo.org/linked-data/def/cpc/sortKey</td>
                        </tr>
                        <tr>
                            <td>sourcePublication</td>
                            <td>https://data.epo.org/linked-data/def/patent/sourcePublication</td>
                        </tr>
                        <tr>
                            <td>streetAddress</td>
                            <td>https://www.w3.org/2006/vcard/ns#street-address</td>
                        </tr>
                        <tr>
                            <td>subClassOf</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#subClassOf</td>
                        </tr>
                        <tr>
                            <td>subPropertyOf</td>
                            <td>https://www.w3.org/2000/01/rdf-schema#subPropertyOf</td>
                        </tr>
                        <tr>
                            <td>title</td>
                            <td>https://purl.org/dc/terms/title</td>
                        </tr>
                        <tr>
                            <td>titleOfInvention</td>
                            <td>https://data.epo.org/linked-data/def/patent/titleOfInvention</td>
                        </tr>
                        <tr>
                            <td>titleXML</td>
                            <td>https://data.epo.org/linked-data/def/cpc/titleXML</td>
                        </tr>
                        <tr>
                            <td>topConceptOf</td>
                            <td>https://www.w3.org/2004/02/skos/core#topConceptOf</td>
                        </tr>
                        <tr>
                            <td>type</td>
                            <td>https://www.w3.org/1999/02/22-rdf-syntax-ns#type</td>
                        </tr>
                        <tr>
                            <td>vcAgentOf</td>
                            <td>https://data.epo.org/linked-data/def/patent/vcAgentOf</td>
                        </tr>
                        <tr>
                            <td>vcApplicantOf</td>
                            <td>https://data.epo.org/linked-data/def/patent/vcApplicantOf</td>
                        </tr>
                        <tr>
                            <td>vcInventorOf</td>
                            <td>https://data.epo.org/linked-data/def/patent/vcInventorOf</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <Terms />
        </Container>
    );
};

export default APIReferencePage;
